import { decodeJwtPayload } from './jwt'

/**
 * App Roles on the API registration (sp-tis-{env}-keystone-api). They appear
 * in the **access token's** `roles` claim, not the ID token's, because App
 * Roles are emitted for the audience they are assigned on.
 *
 * The API is the authority (api/src/authorisation.ts, docs/authorisation.md);
 * the SPA reads these only to decide which controls to render. The hierarchy
 * matches the API's: Admin implies every other role, and any of the five
 * implies User.
 */
export type AppRole = 'Admin' | 'Manager' | 'Shaper' | 'Allocator' | 'User'

export const APP_ROLES: readonly AppRole[] = ['Admin', 'Manager', 'Shaper', 'Allocator', 'User']
const KNOWN: readonly string[] = APP_ROLES

export interface Roles {
  /** Holds any of the five roles: may use the app at all. */
  isReader: boolean
  isManager: boolean
  isAdministrator: boolean
  /** Creates initiatives, promotes ideas and moves initiatives through the funnel. */
  isShaper: boolean
  /** Allocates people to initiatives in Allocate and Implement. Its own role since design v2. */
  isAllocator: boolean
  /** Raw roles array as it appeared in the token, filtered to known values. */
  raw: AppRole[]
}

/** Signed out, or a token with none of the five roles. */
export const NO_ROLES: Roles = {
  isReader: false,
  isManager: false,
  isAdministrator: false,
  isShaper: false,
  isAllocator: false,
  raw: [],
}

/** Everything on, for the local dev identity only. */
export const ALL_ROLES: Roles = {
  isReader: true,
  isManager: true,
  isAdministrator: true,
  isShaper: true,
  isAllocator: true,
  raw: ['Admin'],
}

/**
 * Extract the roles from an access token's payload. Case-sensitive: Entra
 * emits the App Role `value` exactly as configured, not its display name.
 */
export function getRolesFromAccessToken(token: string | null | undefined): Roles {
  if (!token) return NO_ROLES

  const payload = decodeJwtPayload(token)
  if (!payload) return NO_ROLES

  const rawRoles = payload['roles']
  if (!Array.isArray(rawRoles)) return NO_ROLES

  return rolesFrom(rawRoles)
}

/** The flags for a list of role values; anything not one of the five is dropped. */
export function rolesFrom(values: readonly unknown[]): Roles {
  const roles = values.filter((r): r is AppRole => typeof r === 'string' && KNOWN.includes(r))
  const admin = roles.includes('Admin')
  const shaper = admin || roles.includes('Shaper')

  return {
    isReader: roles.length > 0,
    isManager: admin || roles.includes('Manager'),
    isAdministrator: admin,
    isShaper: shaper,
    isAllocator: admin || roles.includes('Allocator'),
    raw: roles,
  }
}

/**
 * May this identity promote an idea into an initiative? Promotion lands at
 * `Shaped`, which is G0, which adr/002 gives to shapers.
 */
export function canPromote(roles: Roles): boolean {
  return roles.isShaper
}

/** May this identity allocate people to an initiative? */
export function canAllocate(roles: Roles): boolean {
  return roles.isAllocator
}
